import { PageHeader } from '@repo/ui/components/page-header'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import { CallStateBadge } from '@/routes/(private)/-components/call-state-badge'
import { useWorkingCall } from '../../-hooks/use-working-call'
import { workingCallOf } from '../../-lib/working-call'
import {
	managedCallsQueryOptions,
	managerHomeQueryOptions
} from '../../-query/query-options'
import { ActivityCounters } from './-components/activity-counters'
import { ActivityPeriod } from './-components/activity-period'
import { DsuQuotaBox } from './-components/dsu-quota-box'
import { NewsSection } from './-components/news-section'
import { PendingTasks } from './-components/pending-tasks'

export const Route = createFileRoute('/(private)/bando/$residenceId/attivita/')(
	{
		loaderDeps: ({ search }) => ({ anno: search.anno }),
		loader: async ({ context, params, deps }) => {
			const list = await context.queryClient.ensureQueryData(
				managedCallsQueryOptions()
			)
			const { callId } = workingCallOf(
				list,
				params.residenceId,
				deps.anno
			)
			return context.queryClient.ensureQueryData({
				...managerHomeQueryOptions(params.residenceId, callId),
				revalidateIfStale: true
			})
		},
		component: ActivityPage
	}
)

/**
 * The first page of the manager for one residence: where the call is, how many
 * applications stand in each state and what waits, the soonest deadline first.
 * Nothing appears without data behind it.
 */
function ActivityPage() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const { callId, readOnly, isCurrent } = useWorkingCall()
	const { data } = useSuspenseQuery(
		managerHomeQueryOptions(residenceId, callId)
	)

	return (
		<main className='flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-4 md:p-6'>
			<PageHeader title={t('admin-call:activity.page_title')} />
			<section
				aria-labelledby='activity-call-title'
				className='flex flex-col gap-2'
			>
				<h2
					className='font-semibold text-base'
					id='activity-call-title'
				>
					{t('admin-call:activity.call_title')}
				</h2>
				<div className='flex flex-wrap items-center gap-3'>
					<CallStateBadge state={data.call.state} />
					<ActivityPeriod call={data.call} />
				</div>
			</section>
			{!readOnly && (
				<PendingTasks residenceId={residenceId} tasks={data.tasks} />
			)}
			{data.dsuQuota && isCurrent && (
				<DsuQuotaBox quota={data.dsuQuota} residenceId={residenceId} />
			)}
			<NewsSection news={data.news} residenceId={residenceId} />
			<ActivityCounters
				counters={data.counters}
				intake={data.call.intake}
				residenceId={residenceId}
			/>
		</main>
	)
}
